import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  useTheme,
  Autocomplete,
  TextField,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

// Icons
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ClearIcon from '@mui/icons-material/Clear';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import DownloadIcon from '@mui/icons-material/Download';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

// Components
import FilterPanel from '../components/common/FilterPanel';
import ChartContainer from '../components/common/ChartContainer';
import { buildFilterLabel } from '../utils/buildFilterLabel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend
);

// ─── Reusable mini-table that mirrors chart data with CSV download ───────────
const ChartDataTable = ({ headers, rows, filename, accentColor }) => {
  const theme = useTheme();
  if (!rows || rows.length === 0) return null;

  const downloadCSV = () => {
    const csvRows = [
      headers.join(','),
      ...rows.map((r) =>
        r.map((c) => (typeof c === 'string' && c.includes(',') ? `"${c}"` : c)).join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename || 'data'}.csv`;
    a.click();
  };

  const color = accentColor || theme.palette.primary.main;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <Tooltip title="Download CSV">
          <IconButton size="small" onClick={downloadCSV} sx={{ color }}>
            <DownloadIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${alpha(color, 0.15)}` }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableCell
                    key={h}
                    align={i === 0 ? 'left' : 'right'}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: color,
                      color: '#fff',
                      fontSize: '0.75rem',
                      py: 0.6,
                      px: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, ri) => (
                <TableRow
                  key={ri}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: alpha(color, 0.03) },
                  }}
                >
                  {row.map((cell, ci) => (
                    <TableCell
                      key={ci}
                      align={ci === 0 ? 'left' : 'right'}
                      sx={{ fontSize: '0.8rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }}
                    >
                      {typeof cell === 'number' ? cell.toLocaleString() : cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

// ─── Color config per vehicle type ───────────────────────────────────────────
const THEME_CONFIG = {
  ev: {
    primary: 'rgba(54, 162, 235, 0.7)',
    border: 'rgba(54, 162, 235, 1)',
    makerLine: 'rgba(153, 102, 255, 0.7)',
    makerLineBorder: 'rgba(153, 102, 255, 1)',
    label: 'EV',
    apiKey: 'ev',
    icon: <BoltIcon fontSize="small" />,
  },
  ice: {
    primary: 'rgba(255, 99, 132, 0.7)',
    border: 'rgba(255, 99, 132, 1)',
    makerLine: 'rgba(75, 192, 192, 0.7)',
    makerLineBorder: 'rgba(75, 192, 192, 1)',
    label: 'ICE',
    apiKey: 'ice',
    icon: <LocalGasStationIcon fontSize="small" />,
  },
};

const COMBINED_CONFIG = {
  primary: 'rgba(99, 102, 241, 0.7)',
  border: 'rgba(79, 70, 229, 1)',
  makerLine: 'rgba(129, 140, 248, 0.7)',
  makerLineBorder: 'rgba(99, 102, 241, 1)',
  label: 'EV + ICE',
  apiKey: 'both',
  icon: (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      <BoltIcon sx={{ fontSize: '1rem' }} />
      <LocalGasStationIcon sx={{ fontSize: '1rem' }} />
    </Box>
  ),
};

function mergeSalesByMaker(rowArrays) {
  const map = new Map();
  for (const arr of rowArrays) {
    for (const r of arr) {
      const id = r._id;
      map.set(id, (map.get(id) || 0) + (Number(r.totalSales) || 0));
    }
  }
  return [...map.entries()]
    .map(([id, totalSales]) => ({ _id: id, totalSales, maker: id }))
    .sort((a, b) => b.totalSales - a.totalSales);
}

function mergeMonthlySeries(arrays) {
  if (arrays.length === 1) return arrays[0];
  const byMonth = new Map();
  const order = arrays[0].map((d) => d.month);
  for (const arr of arrays) {
    for (const d of arr) {
      byMonth.set(d.month, (byMonth.get(d.month) || 0) + (Number(d.sales) || 0));
    }
  }
  return order.map((month) => ({ month, sales: byMonth.get(month) || 0 }));
}

function mergeMakerMonthly(arrays) {
  if (arrays.length === 1) return arrays[0];
  const byShort = new Map();
  for (const arr of arrays) {
    for (const d of arr) {
      const prev = byShort.get(d.shortMonth);
      byShort.set(d.shortMonth, {
        month: d.month,
        shortMonth: d.shortMonth,
        sales: (prev?.sales || 0) + (Number(d.sales) || 0),
      });
    }
  }
  const order = arrays[0].map((d) => d.shortMonth);
  return order.map((sm) => {
    const row = byShort.get(sm);
    return row || { shortMonth: sm, month: '', sales: 0 };
  });
}

function mergeYearlyRows(rowsList, groupBy, subGroup, subSubGroup) {
  if (rowsList.length === 1) return rowsList[0];
  const keyFn = (d) => {
    // RTO names are not unique, so include the RTO code in the key when present
    // to avoid merging different RTO offices that share the same name.
    const codePart = d.rtoCode != null ? `|#${d.rtoCode}` : '';
    if (
      subSubGroup && d[subSubGroup] !== undefined &&
      subGroup && d[subGroup] !== undefined &&
      groupBy && d[groupBy] !== undefined
    ) {
      return `${d.year}|${d[groupBy]}${codePart}|${d[subGroup]}|${d[subSubGroup]}`;
    }
    if (subGroup && d.maker !== undefined && groupBy && d[groupBy] !== undefined) {
      return `${d.year}|${d[groupBy]}${codePart}|${d[subGroup]}`;
    }
    if (groupBy && d[groupBy] !== undefined) {
      return `${d.year}|${d[groupBy]}${codePart}`;
    }
    return String(d.year);
  };
  const map = new Map();
  for (const rows of rowsList) {
    for (const d of rows) {
      const k = keyFn(d);
      if (!map.has(k)) {
        map.set(k, {
          ...d,
          monthlyData: (d.monthlyData || []).map((m) => ({ ...m })),
        });
      } else {
        const ex = map.get(k);
        ex.totalSales = (ex.totalSales || 0) + (d.totalSales || 0);
        const md = ex.monthlyData || [];
        const incoming = d.monthlyData || [];
        ex.monthlyData = md.map((m, i) => ({
          month: m.month,
          sales: (Number(m.sales) || 0) + (Number(incoming[i]?.sales) || 0),
        }));
      }
    }
  }
  return Array.from(map.values());
}

function mergeCompareMakers(arrays) {
  if (arrays.length === 1) return arrays[0];
  const byMaker = new Map();
  for (const arr of arrays) {
    for (const entry of arr) {
      const m = entry.maker;
      if (!byMaker.has(m)) {
        byMaker.set(m, {
          maker: m,
          monthlyData: (entry.monthlyData || []).map((x) => ({ ...x })),
        });
      } else {
        const ex = byMaker.get(m);
        const inc = entry.monthlyData || [];
        ex.monthlyData = ex.monthlyData.map((mo, i) => ({
          ...mo,
          sales: (Number(mo.sales) || 0) + (Number(inc[i]?.sales) || 0),
        }));
      }
    }
  }
  return Array.from(byMaker.values());
}

// ─── Shared chart options factory ─────────────────────────────────────────────
const makeChartOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => `Sales: ${ctx.raw.toLocaleString()}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { callback: (v) => v.toLocaleString() },
    },
  },
  ...extra,
});

// ─── Main Component ───────────────────────────────────────────────────────────
const VehicleAnalytics = () => {
  const theme = useTheme();
  const contentRef = useRef(null);

  // Chart refs
  const makersChartRef = useRef(null);
  const monthlyTrendChartRef = useRef(null);
  const comparisonChartRef = useRef(null);
  const yearlyChartRef = useRef(null);
  const geoDimChartRef = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [tabValue, setTabValue] = useState(0);           // 0 = Overview, 1 = Compare
  const [viewType, setViewType] = useState('bar');
  const [expanded, setExpanded] = useState(true);
  const [lastUpdated, setLastUpdated] = useState({ ev: '—', ice: '—' });

  const [filters, setFilters] = useState({ year: '', state: '', maker: '', vehicleType: 'ev' });

  const splitFilter = (v) => (v ? String(v).split(',').filter(Boolean) : []);
  const selectedTypes = useMemo(() => {
    const t = splitFilter(filters.vehicleType);
    return t.length ? t : ['ev'];
  }, [filters.vehicleType]);

  const cfg = useMemo(() => {
    if (selectedTypes.length > 1) return COMBINED_CONFIG;
    return THEME_CONFIG[selectedTypes[0]] || THEME_CONFIG.ev;
  }, [selectedTypes]);

  const [salesData, setSalesData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [makerMonthlyData, setMakerMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  // 3-level (State × City × Maker) data — used only by the breakdown pivot table
  // when state and maker are both multi-selected, to add a City column.
  const [yearlyDataWithCity, setYearlyDataWithCity] = useState([]);
  const [allMakers, setAllMakers] = useState([]);
  const [selectedMakers, setSelectedMakers] = useState([]);
  const [compareData, setCompareData] = useState([]);

  // Per-type raw data — only populated when both types are selected
  const [evSalesData, setEvSalesData] = useState([]);
  const [iceSalesData, setIceSalesData] = useState([]);
  const [evMonthlyData, setEvMonthlyData] = useState([]);
  const [iceMonthlyData, setIceMonthlyData] = useState([]);
  const [evMakerMonthlyData, setEvMakerMonthlyData] = useState([]);
  const [iceMakerMonthlyData, setIceMakerMonthlyData] = useState([]);
  const [evYearlyData, setEvYearlyData] = useState([]);
  const [iceYearlyData, setIceYearlyData] = useState([]);

  // Drill-down data for geo-dimension table (e.g., all RTOs when a state is selected)
  const [geoDrilldownData, setGeoDrilldownData] = useState([]);
  const [evGeoDrilldownData, setEvGeoDrilldownData] = useState([]);
  const [iceGeoDrilldownData, setIceGeoDrilldownData] = useState([]);

  // Per-dim × maker breakdown for the ranked geo-dim table (powers maker columns)
  const [geoDimMakerData, setGeoDimMakerData] = useState([]);
  const [evGeoDimMakerData, setEvGeoDimMakerData] = useState([]);
  const [iceGeoDimMakerData, setIceGeoDimMakerData] = useState([]);

  // Always-available geo breakdowns (separate charts): City and Sub District
  // Note: label swap in UI — "City" maps to `district` key; "Sub District" maps to `city` key.
  const [cityDimData, setCityDimData] = useState([]);
  const [subDistrictDimData, setSubDistrictDimData] = useState([]);
  const [evCityDimData, setEvCityDimData] = useState([]);
  const [iceCityDimData, setIceCityDimData] = useState([]);
  const [evSubDistrictDimData, setEvSubDistrictDimData] = useState([]);
  const [iceSubDistrictDimData, setIceSubDistrictDimData] = useState([]);

  const isCombined = selectedTypes.length > 1;

  const [isLoading, setIsLoading] = useState({
    makers: true,
    monthlyTrend: true,
    tableData: true,
    makerMonthly: true,
    comparison: true,
    yearly: false,
  });

  // ── Fetch all makers for dropdown ─────────────────────────────────────────
  useEffect(() => {
    const fetchMakers = async () => {
      try {
        const res = await axios.get('/api/makers');
        const evKeywords = ['electric', 'ev', 'battery', 'ather', 'ola'];
        const evMakers = res.data.filter((m) => evKeywords.some((k) => m.toLowerCase().includes(k)));
        const iceMakers = res.data.filter((m) => !evKeywords.some((k) => m.toLowerCase().includes(k)));
        if (selectedTypes.length > 1) {
          setAllMakers([...new Set([...evMakers, ...iceMakers])].sort());
        } else if (selectedTypes[0] === 'ev') {
          setAllMakers(evMakers);
        } else {
          setAllMakers(iceMakers);
        }
      } catch (e) {
        console.error('Error fetching makers:', e);
      }
    };
    fetchMakers();
  }, [selectedTypes]);

  // Detect multi-value selections
  const selectedYears = splitFilter(filters.year);
  const isMultiYear = selectedYears.length > 1;

  // Filename format: vahan_master_sheet_<EV_DATE>,<ICE_DATE>.xlsx (e.g. ..._25-05-26,22-05-26.xlsx)
  // First DD-MM-YY = EV, second = ICE.
  const extractDateFromExcelName = (fileName) => {
    if (typeof fileName !== 'string') return { ev: null, ice: null };
    const matches = fileName.match(/\d{2}-\d{2}-\d{2}/g) || [];
    return { ev: matches[0] || null, ice: matches[1] || null };
  };

  // Find the first multi-value geo/maker filter for the breakdown groupBy
  const geoFilterDims = [
    { key: 'state',    label: 'State' },
    { key: 'city',     label: 'Sub District' },
    { key: 'district', label: 'City' },
    { key: 'rto',      label: 'RTO' },
    { key: 'rtoCode',  label: 'RTO Code' },
  ];
  const activeMultiDim = [...geoFilterDims, { key: 'maker', label: 'Maker' }].find(
    (d) => splitFilter(filters[d.key]).length > 1
  );
  // Detect if maker is also multi-selected separately from the geo dimension
  const isMultiMaker = splitFilter(filters.maker).length > 1;
  const hasGeoAndMaker = activeMultiDim && activeMultiDim.key !== 'maker' && isMultiMaker;

  // Show breakdown charts when multiple years OR any filter has multiple values
  const showBreakdown = isMultiYear || !!activeMultiDim;

  // Drill-down: when a parent geo is selected (even single), show child-level breakdown
  const geoDrilldownDim = useMemo(() => {
    if (activeMultiDim) return null;
    const hasRto = splitFilter(filters.rto).length > 0;
    const hasRtoCode = splitFilter(filters.rtoCode).length > 0;
    if (hasRto || hasRtoCode) return null;
    const hasState = splitFilter(filters.state).length > 0;
    const hasDistrict = splitFilter(filters.district).length > 0;
    const hasCity = splitFilter(filters.city).length > 0;
    if (hasState || hasDistrict || hasCity) return { key: 'rto', label: 'RTO' };
    return null;
  }, [filters, activeMultiDim]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch main data ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading((p) => ({ ...p, makers: true, monthlyTrend: true, tableData: true, yearly: showBreakdown }));
      try {
        const commonParams = {
          year: filters.year, state: filters.state,
          rtoCode: filters.rtoCode, rto: filters.rto,
          city: filters.city, district: filters.district,
          ...(filters.maker ? { maker: filters.maker } : {}),
          ...(filters.month ? { month: filters.month } : {}),
        };
        const types = selectedTypes;
        const multiType = types.length > 1;

        let sales;
        let monthly;
        if (!multiType) {
          const t = types[0];
          const [salesRes, monthlyRes] = await Promise.all([
            axios.get(`/api/sales/${t}`, { params: commonParams }),
            axios.get('/api/sales/monthly', { params: { ...commonParams, type: t } }),
          ]);
          sales = salesRes.data;
          monthly = monthlyRes.data;
          setEvSalesData([]); setIceSalesData([]);
          setEvMonthlyData([]); setIceMonthlyData([]);
        } else {
          const [evS, iceS, evM, iceM] = await Promise.all([
            axios.get('/api/sales/ev', { params: commonParams }),
            axios.get('/api/sales/ice', { params: commonParams }),
            axios.get('/api/sales/monthly', { params: { ...commonParams, type: 'ev' } }),
            axios.get('/api/sales/monthly', { params: { ...commonParams, type: 'ice' } }),
          ]);
          setEvSalesData(evS.data); setIceSalesData(iceS.data);
          setEvMonthlyData(evM.data); setIceMonthlyData(iceM.data);
          sales = mergeSalesByMaker([evS.data, iceS.data]);
          monthly = mergeMonthlySeries([evM.data, iceM.data]);
        }
        setSalesData(sales);
        setMonthlyData(monthly);

        if (filters.maker) {
          if (!multiType) {
            const mm = await axios.get('/api/sales/maker-monthly', {
              params: { ...commonParams, type: types[0] },
            });
            setMakerMonthlyData(mm.data);
            setEvMakerMonthlyData([]); setIceMakerMonthlyData([]);
          } else {
            const [evMm, iceMm] = await Promise.all([
              axios.get('/api/sales/maker-monthly', { params: { ...commonParams, type: 'ev' } }),
              axios.get('/api/sales/maker-monthly', { params: { ...commonParams, type: 'ice' } }),
            ]);
            setEvMakerMonthlyData(evMm.data); setIceMakerMonthlyData(iceMm.data);
            setMakerMonthlyData(mergeMakerMonthly([evMm.data, iceMm.data]));
          }
        } else {
          setMakerMonthlyData([]);
          setEvMakerMonthlyData([]); setIceMakerMonthlyData([]);
        }

        if (showBreakdown) {
          const yearlyParams = { ...commonParams };
          if (activeMultiDim) yearlyParams.groupBy = activeMultiDim.key;
          if (hasGeoAndMaker) yearlyParams.subGroup = 'maker';
          if (!multiType) {
            const yearlyRes = await axios.get('/api/sales/yearly', {
              params: { ...yearlyParams, type: types[0] },
            });
            setYearlyData(yearlyRes.data);
            setEvYearlyData([]); setIceYearlyData([]);
          } else {
            const [yEv, yIce] = await Promise.all([
              axios.get('/api/sales/yearly', { params: { ...yearlyParams, type: 'ev' } }),
              axios.get('/api/sales/yearly', { params: { ...yearlyParams, type: 'ice' } }),
            ]);
            setEvYearlyData(yEv.data); setIceYearlyData(yIce.data);
            setYearlyData(
              mergeYearlyRows(
                [yEv.data, yIce.data],
                activeMultiDim?.key,
                hasGeoAndMaker ? 'maker' : undefined
              )
            );
          }

          // Extra fetch: when the table pivot is State × Maker, also fetch
          // State × City × Maker data so the table can show a City column.
          // Kept as a separate dataset to avoid changing the shape of `yearlyData`
          // (which other charts/tables depend on).
          if (hasGeoAndMaker && activeMultiDim?.key === 'state') {
            const cityPivotParams = {
              ...commonParams,
              groupBy: 'state',
              subGroup: 'district',
              subSubGroup: 'maker',
            };
            if (!multiType) {
              const r = await axios.get('/api/sales/yearly', {
                params: { ...cityPivotParams, type: types[0] },
              });
              setYearlyDataWithCity(r.data || []);
            } else {
              const [rEv, rIce] = await Promise.all([
                axios.get('/api/sales/yearly', { params: { ...cityPivotParams, type: 'ev' } }),
                axios.get('/api/sales/yearly', { params: { ...cityPivotParams, type: 'ice' } }),
              ]);
              setYearlyDataWithCity(
                mergeYearlyRows([rEv.data || [], rIce.data || []], 'state', 'district', 'maker')
              );
            }
          } else {
            setYearlyDataWithCity([]);
          }
        } else {
          setYearlyData([]); setEvYearlyData([]); setIceYearlyData([]);
          setYearlyDataWithCity([]);
        }

        // Drill-down fetch: e.g. all RTOs when a state is selected (even single)
        if (geoDrilldownDim) {
          const drillParams = { ...commonParams, groupBy: geoDrilldownDim.key };
          if (!multiType) {
            const drillRes = await axios.get('/api/sales/yearly', {
              params: { ...drillParams, type: types[0] },
            });
            setGeoDrilldownData(drillRes.data);
            setEvGeoDrilldownData([]); setIceGeoDrilldownData([]);
          } else {
            const [evDrill, iceDrill] = await Promise.all([
              axios.get('/api/sales/yearly', { params: { ...drillParams, type: 'ev' } }),
              axios.get('/api/sales/yearly', { params: { ...drillParams, type: 'ice' } }),
            ]);
            setEvGeoDrilldownData(evDrill.data); setIceGeoDrilldownData(iceDrill.data);
            setGeoDrilldownData(
              mergeYearlyRows([evDrill.data, iceDrill.data], geoDrilldownDim.key)
            );
          }
        } else {
          setGeoDrilldownData([]); setEvGeoDrilldownData([]); setIceGeoDrilldownData([]);
        }

        // Per-dim × maker breakdown — feeds the maker columns in the ranked geo-dim table
        const dimForMakerCols = activeMultiDim ? activeMultiDim.key : (geoDrilldownDim ? geoDrilldownDim.key : null);
        if (dimForMakerCols) {
          const makerColParams = { ...commonParams, groupBy: dimForMakerCols, subGroup: 'maker' };
          if (!multiType) {
            const r = await axios.get('/api/sales/yearly', { params: { ...makerColParams, type: types[0] } });
            setGeoDimMakerData(r.data || []);
            setEvGeoDimMakerData([]); setIceGeoDimMakerData([]);
          } else {
            const [evRes, iceRes] = await Promise.all([
              axios.get('/api/sales/yearly', { params: { ...makerColParams, type: 'ev' } }),
              axios.get('/api/sales/yearly', { params: { ...makerColParams, type: 'ice' } }),
            ]);
            setEvGeoDimMakerData(evRes.data || []); setIceGeoDimMakerData(iceRes.data || []);
            setGeoDimMakerData(mergeYearlyRows([evRes.data || [], iceRes.data || []], dimForMakerCols, 'maker'));
          }
        } else {
          setGeoDimMakerData([]); setEvGeoDimMakerData([]); setIceGeoDimMakerData([]);
        }

        // Separate geo charts for City and Sub District (same filters)
        // We keep these independent of the "effectiveGeoDim" logic so they always show.
        const cityParams = { ...commonParams, groupBy: 'district' }; // UI label: City
        const subDistrictParams = { ...commonParams, groupBy: 'city' }; // UI label: Sub District
        if (!multiType) {
          const [cityRes, subDistRes] = await Promise.all([
            axios.get('/api/sales/yearly', { params: { ...cityParams, type: types[0] } }),
            axios.get('/api/sales/yearly', { params: { ...subDistrictParams, type: types[0] } }),
          ]);
          setCityDimData(cityRes.data || []);
          setSubDistrictDimData(subDistRes.data || []);
          setEvCityDimData([]); setIceCityDimData([]);
          setEvSubDistrictDimData([]); setIceSubDistrictDimData([]);
        } else {
          const [evCityRes, iceCityRes, evSubRes, iceSubRes] = await Promise.all([
            axios.get('/api/sales/yearly', { params: { ...cityParams, type: 'ev' } }),
            axios.get('/api/sales/yearly', { params: { ...cityParams, type: 'ice' } }),
            axios.get('/api/sales/yearly', { params: { ...subDistrictParams, type: 'ev' } }),
            axios.get('/api/sales/yearly', { params: { ...subDistrictParams, type: 'ice' } }),
          ]);
          setEvCityDimData(evCityRes.data || []); setIceCityDimData(iceCityRes.data || []);
          setEvSubDistrictDimData(evSubRes.data || []); setIceSubDistrictDimData(iceSubRes.data || []);
          setCityDimData(mergeYearlyRows([evCityRes.data || [], iceCityRes.data || []], 'district'));
          setSubDistrictDimData(mergeYearlyRows([evSubRes.data || [], iceSubRes.data || []], 'city'));
        }
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setIsLoading((p) => ({ ...p, makers: false, monthlyTrend: false, tableData: false, makerMonthly: false, yearly: false }));
      }
    };
    fetchData();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps -- breakdown dims derived from filters

  // ── Fetch comparison data ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchComparison = async () => {
      if (!selectedMakers.length) { setCompareData([]); return; }
      setIsLoading((p) => ({ ...p, comparison: true }));
      try {
        const base = {
          year: filters.year, state: filters.state,
          rtoCode: filters.rtoCode, rto: filters.rto,
          city: filters.city, district: filters.district,
          makers: selectedMakers.join(','),
          ...(filters.month ? { month: filters.month } : {}),
        };
        const types = selectedTypes;
        if (types.length === 1) {
          const res = await axios.get('/api/sales/compare-makers', {
            params: { ...base, type: types[0] },
          });
          setCompareData(res.data);
        } else {
          const [evC, iceC] = await Promise.all([
            axios.get('/api/sales/compare-makers', { params: { ...base, type: 'ev' } }),
            axios.get('/api/sales/compare-makers', { params: { ...base, type: 'ice' } }),
          ]);
          setCompareData(mergeCompareMakers([evC.data, iceC.data]));
        }
      } catch (e) {
        console.error('Error fetching comparison:', e);
      } finally {
        setIsLoading((p) => ({ ...p, comparison: false }));
      }
    };
    fetchComparison();
  }, [selectedMakers, filters]);

  useEffect(() => {
    const fetchExcelLastUpdated = async () => {
      try {
        const params =
          selectedYears.length === 1 && selectedYears[0]
            ? { year: selectedYears[0] }
            : {};
        const { data } = await axios.get('/api/dashboard/excel-files', { params });
        const files = Array.isArray(data?.files) ? data.files : [];
        const parsed = extractDateFromExcelName(files[0]?.name);
        setLastUpdated({ ev: parsed.ev || '—', ice: parsed.ice || '—' });
      } catch (e) {
        console.error('Error fetching excel last updated:', e);
        setLastUpdated({ ev: '—', ice: '—' });
      }
    };

    fetchExcelLastUpdated();
  }, [filters.year]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFilterChange = (newFilters) => {
    const prevTypes = splitFilter(filters.vehicleType).sort().join(',');
    const nextTypes = splitFilter(newFilters.vehicleType || '').sort().join(',');
    if (prevTypes !== nextTypes) {
      setSelectedMakers([]);
      setCompareData([]);
      setTabValue(0);
    }
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setExpanded(false);
    setTimeout(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, 100);
  };

  const handleAddMaker = (maker) => {
    if (maker && !selectedMakers.includes(maker))
      setSelectedMakers((prev) => [...prev, maker]);
  };
  const handleRemoveMaker = (maker) =>
    setSelectedMakers((prev) => prev.filter((m) => m !== maker));

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const rows = isCombined
      ? [
          ['Rank', 'Maker', 'EV Sales', 'ICE Sales', 'Total Sales'],
          ...salesData.map((r, i) => [
            i + 1,
            `"${r._id.replace(/"/g, '""')}"`,
            evSalesMap.get(r._id) || 0,
            iceSalesMap.get(r._id) || 0,
            r.totalSales,
          ]),
        ]
      : [
          ['Rank', 'Maker', 'Total Sales'],
          ...salesData.map((r, i) => [i + 1, `"${r._id.replace(/"/g, '""')}"`, r.totalSales]),
        ];
    const blob = new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${selectedTypes.join('_')}_sales_${filters.year || 'all'}.csv`;
    a.click();
  };

  // ── Helper: look up per-type sales for a maker ──────────────────────────
  const evSalesMap = useMemo(() => {
    const m = new Map();
    evSalesData.forEach((r) => m.set(r._id, r.totalSales || 0));
    return m;
  }, [evSalesData]);

  const iceSalesMap = useMemo(() => {
    const m = new Map();
    iceSalesData.forEach((r) => m.set(r._id, r.totalSales || 0));
    return m;
  }, [iceSalesData]);

  // ── Chart data ─────────────────────────────────────────────────────────────
  const top10 = salesData.slice(0, 10);
  const makersChartData = isCombined
    ? {
        labels: top10.map((d) => d._id),
        datasets: [
          {
            label: 'EV Sales',
            data: top10.map((d) => evSalesMap.get(d._id) || 0),
            backgroundColor: THEME_CONFIG.ev.primary,
            borderColor: THEME_CONFIG.ev.border,
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: top10.map((d) => iceSalesMap.get(d._id) || 0),
            backgroundColor: THEME_CONFIG.ice.primary,
            borderColor: THEME_CONFIG.ice.border,
            borderWidth: 1,
          },
        ],
      }
    : {
        labels: top10.map((d) => d._id),
        datasets: [{
          label: `${cfg.label} Sales`,
          data: top10.map((d) => d.totalSales),
          backgroundColor: cfg.primary,
          borderColor: cfg.border,
          borderWidth: 1,
        }],
      };

  const monthlyChartData = isCombined
    ? {
        labels: (evMonthlyData.length ? evMonthlyData : iceMonthlyData).map((d) => d.month.substring(0, 3)),
        datasets: [
          {
            label: 'EV Sales',
            data: evMonthlyData.map((d) => d.sales),
            fill: false,
            backgroundColor: THEME_CONFIG.ev.primary,
            borderColor: THEME_CONFIG.ev.border,
            tension: 0.3,
          },
          {
            label: 'ICE Sales',
            data: iceMonthlyData.map((d) => d.sales),
            fill: false,
            backgroundColor: THEME_CONFIG.ice.primary,
            borderColor: THEME_CONFIG.ice.border,
            tension: 0.3,
          },
        ],
      }
    : {
        labels: monthlyData.map((d) => d.month.substring(0, 3)),
        datasets: [{
          label: `Monthly ${cfg.label} Sales`,
          data: monthlyData.map((d) => d.sales),
          fill: false,
          backgroundColor: cfg.primary,
          borderColor: cfg.border,
          tension: 0.3,
        }],
      };

  const yearlyChartColors = [
    'rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)',
    'rgba(75, 192, 192, 0.7)', 'rgba(255, 206, 86, 0.7)',
    'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
    'rgba(255, 99, 71, 0.7)', 'rgba(46, 139, 87, 0.7)',
    'rgba(106, 90, 205, 0.7)', 'rgba(255, 165, 0, 0.7)',
  ];
  const yearlyBorderColors = yearlyChartColors.map((c) => c.replace('0.7)', '1)'));

  // Build yearly chart data — with optional per-dimension breakdown
  const hasGroupBy = activeMultiDim && yearlyData.length > 0 && yearlyData[0][activeMultiDim.key] !== undefined;
  const hasTwoLevel = hasGeoAndMaker && yearlyData.length > 0 && yearlyData[0].maker !== undefined;
  let yearlyTotalChartData, yearlyMonthlyChartData;

  if (hasTwoLevel) {
    // Two-level: geo dim × maker
    const dimKey = activeMultiDim.key;
    const years = [...new Set(yearlyData.map((d) => String(d.year)))].sort();
    const dims = [...new Set(yearlyData.map((d) => String(d[dimKey])))].sort();
    const makers = [...new Set(yearlyData.map((d) => String(d.maker)))].sort();
    const singleYear = years.length === 1;

    // Bar chart: X-axis = geo values, one dataset per maker
    const barLabels = singleYear
      ? dims
      : dims.flatMap((dim) => years.map((y) => `${dim} (${y})`));

    yearlyTotalChartData = {
      labels: barLabels,
      datasets: makers.map((mk, i) => ({
        label: mk,
        data: singleYear
          ? dims.map((dim) => {
              const rows = yearlyData.filter((d) => String(d[dimKey]) === dim && String(d.maker) === mk);
              return rows.reduce((s, r) => s + (r.totalSales || 0), 0);
            })
          : dims.flatMap((dim) =>
              years.map((y) => {
                const row = yearlyData.find((d) => String(d.year) === y && String(d[dimKey]) === dim && String(d.maker) === mk);
                return row ? row.totalSales : 0;
              })
            ),
        backgroundColor: yearlyChartColors[i % yearlyChartColors.length],
        borderColor: yearlyBorderColors[i % yearlyBorderColors.length],
        borderWidth: 1,
      })),
    };

    const monthLabels = yearlyData.length > 0
      ? yearlyData[0].monthlyData.map((m) => m.month)
      : ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

    // Line chart: one line per "geo — maker" (or "year — geo — maker") combo
    const combos = yearlyData.map((d) => ({
      label: singleYear ? `${d[dimKey]} — ${d.maker}` : `${d.year} — ${d[dimKey]} — ${d.maker}`,
      data: d.monthlyData.map((m) => m.sales),
    }));
    yearlyMonthlyChartData = {
      labels: monthLabels,
      datasets: combos.map((c, i) => ({
        ...c,
        fill: false,
        backgroundColor: yearlyChartColors[i % yearlyChartColors.length],
        borderColor: yearlyBorderColors[i % yearlyBorderColors.length],
        tension: 0.3,
      })),
    };
  } else if (hasGroupBy) {
    const dimKey = activeMultiDim.key;
    const years = [...new Set(yearlyData.map((d) => String(d.year)))].sort();
    const dims = [...new Set(yearlyData.map((d) => String(d[dimKey])))].sort();
    const singleYear = years.length === 1;

    // Build per-type lookup maps when combined
    const evDimTotals = new Map();
    const iceDimTotals = new Map();
    if (isCombined) {
      evYearlyData.forEach((d) => {
        const k = singleYear ? String(d[dimKey]) : `${d.year}|${d[dimKey]}`;
        evDimTotals.set(k, (evDimTotals.get(k) || 0) + (d.totalSales || 0));
      });
      iceYearlyData.forEach((d) => {
        const k = singleYear ? String(d[dimKey]) : `${d.year}|${d[dimKey]}`;
        iceDimTotals.set(k, (iceDimTotals.get(k) || 0) + (d.totalSales || 0));
      });
    }

    if (isCombined && singleYear) {
      yearlyTotalChartData = {
        labels: dims,
        datasets: [
          {
            label: 'EV Sales',
            data: dims.map((dim) => evDimTotals.get(dim) || 0),
            backgroundColor: THEME_CONFIG.ev.primary,
            borderColor: THEME_CONFIG.ev.border,
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: dims.map((dim) => iceDimTotals.get(dim) || 0),
            backgroundColor: THEME_CONFIG.ice.primary,
            borderColor: THEME_CONFIG.ice.border,
            borderWidth: 1,
          },
        ],
      };
    } else if (singleYear) {
      yearlyTotalChartData = {
        labels: dims,
        datasets: [{
          label: `${cfg.label} Sales (${years[0]})`,
          data: dims.map((dim) => {
            const row = yearlyData.find((d) => String(d[dimKey]) === dim);
            return row ? row.totalSales : 0;
          }),
          backgroundColor: dims.map((_, i) => yearlyChartColors[i % yearlyChartColors.length]),
          borderColor: dims.map((_, i) => yearlyBorderColors[i % yearlyBorderColors.length]),
          borderWidth: 1,
        }],
      };
    } else if (isCombined) {
      yearlyTotalChartData = {
        labels: years,
        datasets: [
          {
            label: 'EV Sales',
            data: years.map((y) => {
              let sum = 0;
              evYearlyData.filter((d) => String(d.year) === y).forEach((d) => { sum += d.totalSales || 0; });
              return sum;
            }),
            backgroundColor: THEME_CONFIG.ev.primary,
            borderColor: THEME_CONFIG.ev.border,
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: years.map((y) => {
              let sum = 0;
              iceYearlyData.filter((d) => String(d.year) === y).forEach((d) => { sum += d.totalSales || 0; });
              return sum;
            }),
            backgroundColor: THEME_CONFIG.ice.primary,
            borderColor: THEME_CONFIG.ice.border,
            borderWidth: 1,
          },
        ],
      };
    } else {
      yearlyTotalChartData = {
        labels: years,
        datasets: dims.map((dim, i) => ({
          label: dim,
          data: years.map((y) => {
            const row = yearlyData.find((d) => String(d.year) === y && String(d[dimKey]) === dim);
            return row ? row.totalSales : 0;
          }),
          backgroundColor: yearlyChartColors[i % yearlyChartColors.length],
          borderColor: yearlyBorderColors[i % yearlyBorderColors.length],
          borderWidth: 1,
        })),
      };
    }

    const monthLabels = yearlyData.length > 0
      ? yearlyData[0].monthlyData.map((m) => m.month)
      : ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

    if (isCombined && singleYear) {
      // Separate EV and ICE lines per dimension
      const evDims = [...new Set(evYearlyData.map((d) => String(d[dimKey])))].sort();
      const iceDims = [...new Set(iceYearlyData.map((d) => String(d[dimKey])))].sort();
      const allDims = [...new Set([...evDims, ...iceDims])].sort();
      const datasets = [];
      allDims.forEach((dim, i) => {
        const evRow = evYearlyData.find((d) => String(d[dimKey]) === dim);
        const iceRow = iceYearlyData.find((d) => String(d[dimKey]) === dim);
        if (evRow) {
          datasets.push({
            label: `${dim} — EV`,
            data: evRow.monthlyData.map((m) => m.sales),
            fill: false,
            borderColor: `hsl(${(i * 47) % 360}, 70%, 50%)`,
            backgroundColor: `hsl(${(i * 47) % 360}, 70%, 50%)`,
            tension: 0.3,
            borderWidth: 2,
          });
        }
        if (iceRow) {
          datasets.push({
            label: `${dim} — ICE`,
            data: iceRow.monthlyData.map((m) => m.sales),
            fill: false,
            borderColor: `hsl(${(i * 47) % 360}, 70%, 50%)`,
            backgroundColor: `hsl(${(i * 47) % 360}, 70%, 50%)`,
            borderDash: [6, 3],
            tension: 0.3,
            borderWidth: 2,
          });
        }
      });
      yearlyMonthlyChartData = { labels: monthLabels, datasets };
    } else {
      const combos = yearlyData.map((d) => ({
        label: singleYear ? String(d[dimKey]) : `${d.year} — ${d[dimKey]}`,
        data: d.monthlyData.map((m) => m.sales),
      }));
      yearlyMonthlyChartData = {
        labels: monthLabels,
        datasets: combos.map((c, i) => ({
          ...c,
          fill: false,
          backgroundColor: yearlyChartColors[i % yearlyChartColors.length],
          borderColor: yearlyBorderColors[i % yearlyBorderColors.length],
          tension: 0.3,
        })),
      };
    }
  } else {
    const monthLabels = yearlyData.length > 0
      ? yearlyData[0].monthlyData.map((m) => m.month)
      : ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

    yearlyTotalChartData = {
      labels: yearlyData.map((d) => String(d.year)),
      datasets: [{
        label: `Total ${cfg.label} Sales`,
        data: yearlyData.map((d) => d.totalSales),
        backgroundColor: yearlyData.map((_, i) => yearlyChartColors[i % yearlyChartColors.length]),
        borderColor: yearlyData.map((_, i) => yearlyBorderColors[i % yearlyBorderColors.length]),
        borderWidth: 1,
      }],
    };

    yearlyMonthlyChartData = {
      labels: monthLabels,
      datasets: yearlyData.map((yd, i) => ({
        label: String(yd.year),
        data: yd.monthlyData.map((m) => m.sales),
        fill: false,
        backgroundColor: yearlyChartColors[i % yearlyChartColors.length],
        borderColor: yearlyBorderColors[i % yearlyBorderColors.length],
        tension: 0.3,
      })),
    };
  }

  const comparisonChartData = {
    labels: compareData.length > 0
      ? compareData[0].monthlyData.map((m) => m.month)
      : ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'],
    datasets: compareData.map((makerData, i) => {
      const hue = (i * 137) % 360;
      return {
        label: makerData.maker,
        data: makerData.monthlyData.map((m) => m.sales),
        fill: false,
        backgroundColor: `hsla(${hue},70%,50%,0.7)`,
        borderColor: `hsla(${hue},70%,45%,1)`,
        tension: 0.3,
      };
    }),
  };

  const comparisonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` },
      },
    },
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => v.toLocaleString() } } },
  };

  // ── Table data for charts ──────────────────────────────────────────────────
  const top10MakersTableData = useMemo(() => {
    const top = salesData.slice(0, 10);
    if (!top.length) return { headers: [], rows: [] };
    if (isCombined) {
      const headers = ['Rank', 'Maker', 'EV Sales', 'ICE Sales', 'Total Sales'];
      const rows = top.map((d, i) => [
        i + 1,
        d._id,
        evSalesMap.get(d._id) || 0,
        iceSalesMap.get(d._id) || 0,
        d.totalSales,
      ]);
      return { headers, rows };
    }
    const headers = ['Rank', 'Maker', 'Total Sales'];
    const rows = top.map((d, i) => [i + 1, d._id, d.totalSales]);
    return { headers, rows };
  }, [salesData, isCombined, evSalesMap, iceSalesMap]);

  const monthlyTableData = useMemo(() => {
    if (!monthlyChartData || !monthlyChartData.labels) return { headers: [], rows: [] };
    const labels = monthlyChartData.labels;
    const datasets = monthlyChartData.datasets || [];
    const headers = ['Month', ...datasets.map((ds) => ds.label)];
    const rows = labels.map((lbl, i) => [lbl, ...datasets.map((ds) => ds.data[i] || 0)]);
    rows.push(['Total', ...datasets.map((ds) => ds.data.reduce((s, v) => s + (v || 0), 0))]);
    return { headers, rows };
  }, [monthlyChartData]);

  const breakdownTotalTableData = useMemo(() => {
    if (!showBreakdown || !yearlyData.length) return { headers: [], rows: [] };

    const hasGroupBy = activeMultiDim && yearlyData[0][activeMultiDim.key] !== undefined;
    const hasTwoLevelLocal = hasGeoAndMaker && yearlyData[0].maker !== undefined;

    // Pivot table when both geo dim and maker are multi-selected: rows = dim, cols = makers
    if (hasTwoLevelLocal && activeMultiDim && !isCombined) {
      const dimKey = activeMultiDim.key;
      const totalsMap = new Map(); // city -> Map(maker -> total)
      yearlyData.forEach((d) => {
        const city = String(d[dimKey]);
        const maker = String(d.maker);
        if (!totalsMap.has(city)) totalsMap.set(city, new Map());
        const m = totalsMap.get(city);
        m.set(maker, (m.get(maker) || 0) + (d.totalSales || 0));
      });

      // Sort makers by overall total desc, cities by their total desc
      const makerTotals = new Map();
      const cityTotals = new Map();
      totalsMap.forEach((mMap, city) => {
        let cTotal = 0;
        mMap.forEach((v, mk) => {
          makerTotals.set(mk, (makerTotals.get(mk) || 0) + v);
          cTotal += v;
        });
        cityTotals.set(city, cTotal);
      });
      const makers = [...makerTotals.entries()].sort((a, b) => b[1] - a[1]).map(([mk]) => mk);
      const cities = [...cityTotals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);

      const headers = [activeMultiDim.label, ...makers, 'Total'];
      const rows = cities.map((city) => {
        const m = totalsMap.get(city) || new Map();
        const row = [city];
        makers.forEach((mk) => row.push(m.get(mk) || 0));
        row.push(cityTotals.get(city) || 0);
        return row;
      });
      const grand = [...cityTotals.values()].reduce((s, v) => s + v, 0);
      rows.push(['Total', ...makers.map((mk) => makerTotals.get(mk) || 0), grand]);
      return { headers, rows };
    }

    if (isCombined && hasGroupBy) {
      const dimKey = activeMultiDim.key;
      const dims = [...new Set(yearlyData.map((d) => String(d[dimKey])))].sort();
      const evMap = new Map();
      evYearlyData.forEach((d) => {
        const k = String(d[dimKey]);
        evMap.set(k, (evMap.get(k) || 0) + (d.totalSales || 0));
      });
      const iceMap = new Map();
      iceYearlyData.forEach((d) => {
        const k = String(d[dimKey]);
        iceMap.set(k, (iceMap.get(k) || 0) + (d.totalSales || 0));
      });
      const headers = [activeMultiDim.label, 'EV Sales', 'ICE Sales', 'Total Sales'];
      const rows = dims.map((dim) => {
        const ev = evMap.get(dim) || 0;
        const ice = iceMap.get(dim) || 0;
        return [dim, ev, ice, ev + ice];
      });
      rows.push([
        'Total',
        rows.reduce((s, r) => s + r[1], 0),
        rows.reduce((s, r) => s + r[2], 0),
        rows.reduce((s, r) => s + r[3], 0),
      ]);
      return { headers, rows };
    }

    if (!yearlyTotalChartData || !yearlyTotalChartData.labels) return { headers: [], rows: [] };
    const labels = yearlyTotalChartData.labels;
    const datasets = yearlyTotalChartData.datasets || [];
    if (datasets.length === 1 && !datasets[0].label?.includes('Sales')) {
      const headers = [activeMultiDim?.label || 'Label', 'Total Sales'];
      const rows = labels.map((lbl, i) => [lbl, datasets[0].data[i] || 0]);
      return { headers, rows };
    }
    const headers = [activeMultiDim?.label || 'Year', ...datasets.map((ds) => ds.label)];
    const rows = labels.map((lbl, i) => [lbl, ...datasets.map((ds) => ds.data[i] || 0)]);
    return { headers, rows };
  }, [showBreakdown, yearlyTotalChartData, activeMultiDim, yearlyData, isCombined, evYearlyData, iceYearlyData, hasGeoAndMaker]);

  // Build from yearlyData — do not read labels from Chart.js datasets (Chart mutates dataset.label)
  const breakdownMonthlyTableData = useMemo(() => {
    if (!showBreakdown || !yearlyData.length || !yearlyData[0].monthlyData) return { headers: [], rows: [] };

    const monthLabels = yearlyData[0].monthlyData.map((m) => m.month);
    const hasGroupByLocal = activeMultiDim && yearlyData[0][activeMultiDim.key] !== undefined;
    const hasTwoLevelLocal = hasGeoAndMaker && yearlyData[0].maker !== undefined;

    // Combined + geo breakdown → show EV / ICE columns per dim
    if (isCombined && hasGroupByLocal && !hasTwoLevelLocal && activeMultiDim && evYearlyData.length > 0) {
      const dimKey = activeMultiDim.key;
      const dims = [...new Set(yearlyData.map((d) => String(d[dimKey])))].sort();

      const evMonthByDim = new Map();
      evYearlyData.forEach((d) => {
        const dk = String(d[dimKey]);
        if (!evMonthByDim.has(dk)) evMonthByDim.set(dk, monthLabels.map(() => 0));
        const arr = evMonthByDim.get(dk);
        (d.monthlyData || []).forEach((m, i) => { if (i < arr.length) arr[i] += m.sales || 0; });
      });
      const iceMonthByDim = new Map();
      iceYearlyData.forEach((d) => {
        const dk = String(d[dimKey]);
        if (!iceMonthByDim.has(dk)) iceMonthByDim.set(dk, monthLabels.map(() => 0));
        const arr = iceMonthByDim.get(dk);
        (d.monthlyData || []).forEach((m, i) => { if (i < arr.length) arr[i] += m.sales || 0; });
      });

      const combos = [];
      dims.forEach((dim) => {
        combos.push({ label: `${dim} — EV`, data: evMonthByDim.get(dim) || monthLabels.map(() => 0) });
        combos.push({ label: `${dim} — ICE`, data: iceMonthByDim.get(dim) || monthLabels.map(() => 0) });
      });

      const headers = ['Month', ...combos.map((c) => c.label)];
      const rows = monthLabels.map((lbl, i) => [lbl, ...combos.map((c) => c.data[i] || 0)]);
      rows.push(['Total', ...combos.map((c) => c.data.reduce((s, v) => s + (v || 0), 0))]);
      return { headers, rows };
    }

    // Pivot when both geo dim and maker are multi-selected:
    // rows = (Month × Geo[, City]), cols = Makers.
    // When activeMultiDim is "state" AND `yearlyDataWithCity` is loaded, we
    // expand the pivot to (Month × State × City × Makers) by inserting a City column.
    if (hasTwoLevelLocal && activeMultiDim && !isCombined) {
      const dimKey = activeMultiDim.key;
      const useCity =
        dimKey === 'state' &&
        yearlyDataWithCity.length > 0 &&
        yearlyDataWithCity[0].monthlyData;

      const sourceRows = useCity ? yearlyDataWithCity : yearlyData;

      // salesMap[month][geo][city?][maker] = sales
      const salesMap = new Map();
      const makerTotals = new Map();
      sourceRows.forEach((d) => {
        const geo = String(d[dimKey]);
        const city = useCity ? String(d.district ?? '') : null;
        const maker = String(d.maker);
        (d.monthlyData || []).forEach((m) => {
          if (!m || !m.month) return;
          if (!salesMap.has(m.month)) salesMap.set(m.month, new Map());
          const geoMap = salesMap.get(m.month);
          if (!geoMap.has(geo)) geoMap.set(geo, useCity ? new Map() : new Map());
          const inner = geoMap.get(geo);
          const v = Number(m.sales) || 0;
          if (useCity) {
            if (!inner.has(city)) inner.set(city, new Map());
            const mkMap = inner.get(city);
            mkMap.set(maker, (mkMap.get(maker) || 0) + v);
          } else {
            inner.set(maker, (inner.get(maker) || 0) + v);
          }
          makerTotals.set(maker, (makerTotals.get(maker) || 0) + v);
        });
      });

      const makers = [...makerTotals.entries()].sort((a, b) => b[1] - a[1]).map(([mk]) => mk);
      const orderedMonths = monthLabels.filter((mo) => salesMap.has(mo));

      const headers = useCity
        ? ['Month', activeMultiDim.label, 'City', ...makers, 'Total']
        : ['Month', activeMultiDim.label, ...makers, 'Total'];
      const rows = [];
      const colTotals = makers.map(() => 0);
      let grand = 0;

      orderedMonths.forEach((month) => {
        const geoMap = salesMap.get(month);
        const geoOrder = [...geoMap.entries()]
          .map(([g, inner]) => {
            const total = useCity
              ? [...inner.values()].reduce(
                  (s, mkMap) => s + [...mkMap.values()].reduce((x, v) => x + v, 0),
                  0
                )
              : [...inner.values()].reduce((s, v) => s + v, 0);
            return [g, total];
          })
          .sort((a, b) => b[1] - a[1])
          .map(([g]) => g);

        geoOrder.forEach((geo) => {
          const inner = geoMap.get(geo);
          if (useCity) {
            const cityOrder = [...inner.entries()]
              .map(([c, mkMap]) => [c, [...mkMap.values()].reduce((s, v) => s + v, 0)])
              .sort((a, b) => b[1] - a[1])
              .map(([c]) => c);
            cityOrder.forEach((city) => {
              const mkMap = inner.get(city);
              let rowTotal = 0;
              const row = [month, geo, city];
              makers.forEach((mk, i) => {
                const v = mkMap.get(mk) || 0;
                row.push(v);
                colTotals[i] += v;
                rowTotal += v;
              });
              row.push(rowTotal);
              grand += rowTotal;
              rows.push(row);
            });
          } else {
            const mkMap = inner;
            let rowTotal = 0;
            const row = [month, geo];
            makers.forEach((mk, i) => {
              const v = mkMap.get(mk) || 0;
              row.push(v);
              colTotals[i] += v;
              rowTotal += v;
            });
            row.push(rowTotal);
            grand += rowTotal;
            rows.push(row);
          }
        });
      });

      if (rows.length > 0) {
        const totalRow = useCity
          ? ['Total', '', '', ...colTotals, grand]
          : ['Total', '', ...colTotals, grand];
        rows.push(totalRow);
      }
      return { headers, rows };
    }

    let combos;
    if (hasTwoLevelLocal && activeMultiDim) {
      const dimKey = activeMultiDim.key;
      const years = [...new Set(yearlyData.map((d) => String(d.year)))].sort();
      const singleYear = years.length === 1;
      combos = yearlyData.map((d) => ({
        label: singleYear ? `${d[dimKey]} — ${d.maker}` : `${d.year} — ${d[dimKey]} — ${d.maker}`,
        data: d.monthlyData.map((m) => m.sales),
      }));
    } else if (hasGroupByLocal && activeMultiDim) {
      const dimKey = activeMultiDim.key;
      const years = [...new Set(yearlyData.map((d) => String(d.year)))].sort();
      const singleYear = years.length === 1;
      combos = yearlyData.map((d) => ({
        label: singleYear ? String(d[dimKey]) : `${d.year} — ${d[dimKey]}`,
        data: d.monthlyData.map((m) => m.sales),
      }));
    } else {
      combos = yearlyData.map((yd) => ({
        label: String(yd.year),
        data: yd.monthlyData.map((m) => m.sales),
      }));
    }

    const headers = ['Month', ...combos.map((c) => c.label)];
    const rows = monthLabels.map((lbl, i) => [lbl, ...combos.map((c) => c.data[i] || 0)]);
    rows.push(['Total', ...combos.map((c) => c.data.reduce((s, v) => s + (v || 0), 0))]);
    return { headers, rows };
  }, [showBreakdown, yearlyData, yearlyDataWithCity, activeMultiDim, hasGeoAndMaker, isCombined, evYearlyData, iceYearlyData]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const totalSales = salesData.reduce((s, d) => s + d.totalSales, 0);
  const top10Sales = salesData.slice(0, 10).reduce((s, d) => s + d.totalSales, 0);
  const marketShare = totalSales ? ((top10Sales / totalSales) * 100).toFixed(1) : '—';
  const totalEvSales = evSalesData.reduce((s, d) => s + (d.totalSales || 0), 0);
  const totalIceSales = iceSalesData.reduce((s, d) => s + (d.totalSales || 0), 0);

  const filterInfo = buildFilterLabel(filters, ['year', 'state', 'district', 'city', 'rto', 'rtoCode']);
  const filterLabel = filterInfo.short ? ` — ${filterInfo.short}` : '';
  const filterDescription = filterInfo.full && filterInfo.full !== filterInfo.short ? filterInfo.full : null;

  // ── Geo dimension ranked table (RTO / State / City / etc.) ────────────────
  // effectiveGeoDim: either the multi-selected dim (from breakdown) or the drilldown dim
  const effectiveGeoDim = activeMultiDim || geoDrilldownDim;

  const geoDimTableData = useMemo(() => {
    const dim = effectiveGeoDim;
    if (!dim) return [];

    // Pick the right data source: yearlyData for multi-selected dim, drilldown for single-select parent
    const yData = activeMultiDim ? yearlyData : geoDrilldownData;
    const evYData = activeMultiDim ? evYearlyData : evGeoDrilldownData;
    const iceYData = activeMultiDim ? iceYearlyData : iceGeoDrilldownData;

    if (!yData.length) return [];
    const dimKey = dim.key;
    if (!yData[0] || yData[0][dimKey] === undefined) return [];

    const includeRtoCode = dimKey === 'rto';
    const includeRtoCodeContext = dimKey === 'rtoCode';
    const includeGeoContext = includeRtoCode || includeRtoCodeContext;

    // RTO names are not unique (e.g. "Chennai  Rto" maps to TN1, TN3, TN4 …).
    // When grouping by RTO, key on the unique RTO code so each office stays
    // separate; otherwise group on the dimension value itself.
    const groupKeyOf = (d) => (includeRtoCode ? String(d.rtoCode || d[dimKey]) : String(d[dimKey]));

    const combined = new Map();
    const nameByKey = new Map();  // grouping key -> display name (RTO name)
    const codeByKey = new Map();  // grouping key -> RTO code
    yData.forEach((d) => {
      const k = groupKeyOf(d);
      combined.set(k, (combined.get(k) || 0) + (d.totalSales || 0));
      if (!nameByKey.has(k)) nameByKey.set(k, String(d[dimKey]));
      if (includeRtoCode && !codeByKey.has(k)) {
        codeByKey.set(k, d.rtoCode != null ? String(d.rtoCode) : '');
      }
    });

    let evMap = new Map();
    let iceMap = new Map();
    if (isCombined) {
      evYData.forEach((d) => {
        const k = groupKeyOf(d);
        evMap.set(k, (evMap.get(k) || 0) + (d.totalSales || 0));
      });
      iceYData.forEach((d) => {
        const k = groupKeyOf(d);
        iceMap.set(k, (iceMap.get(k) || 0) + (d.totalSales || 0));
      });
    }

    const firstStringByDim = (field, enabled = includeGeoContext) => {
      if (!enabled) return null;
      const m = new Map();
      yData.forEach((d) => {
        const k = groupKeyOf(d);
        const v = d && d[field] != null && String(d[field]).trim() !== '' ? String(d[field]) : '';
        if (!m.has(k)) m.set(k, v);
        else if (!m.get(k) && v) m.set(k, v);
      });
      return m;
    };
    const districtByDim = firstStringByDim('district');
    const cityByDim = firstStringByDim('city');
    const stateByDim = firstStringByDim('state', includeRtoCodeContext);

    // Maker breakdown per dim (powers the per-maker columns in the table)
    const makerByDim = new Map(); // key -> Map(maker -> total)
    (geoDimMakerData || []).forEach((d) => {
      if (d == null || d[dimKey] == null || d.maker == null) return;
      const k = groupKeyOf(d);
      const mk = String(d.maker);
      if (!makerByDim.has(k)) makerByDim.set(k, new Map());
      const inner = makerByDim.get(k);
      inner.set(mk, (inner.get(mk) || 0) + (Number(d.totalSales) || 0));
    });

    return [...combined.entries()]
      .map(([k, total]) => ({
        dim: nameByKey.get(k) || k,
        ...(includeRtoCode
          ? {
              rtoCode: codeByKey.get(k) || '',
              district: districtByDim?.get(k) || '',
              city: cityByDim?.get(k) || '',
            }
          : {}),
        ...(includeRtoCodeContext
          ? {
              state: stateByDim?.get(k) || '',
              district: districtByDim?.get(k) || '',
              city: cityByDim?.get(k) || '',
            }
          : {}),
        evSales: evMap.get(k) || 0,
        iceSales: iceMap.get(k) || 0,
        totalSales: total,
        makerCounts: makerByDim.get(k) || new Map(),
      }))
      .sort((a, b) => b.totalSales - a.totalSales);
  }, [effectiveGeoDim, activeMultiDim, yearlyData, geoDrilldownData, isCombined,
      evYearlyData, iceYearlyData, evGeoDrilldownData, iceGeoDrilldownData, geoDimMakerData]);

  // Master list of makers for the geo-dim table columns, sorted by overall total desc
  const geoDimMakersList = useMemo(() => {
    if (!geoDimMakerData || geoDimMakerData.length === 0) return [];
    const totals = new Map();
    geoDimMakerData.forEach((d) => {
      if (!d || d.maker == null) return;
      const mk = String(d.maker);
      totals.set(mk, (totals.get(mk) || 0) + (Number(d.totalSales) || 0));
    });
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([mk]) => mk);
  }, [geoDimMakerData]);

  const downloadRowsAsCSV = (headerRow, dataRows, filename) => {
    if (!dataRows || dataRows.length === 0) return;
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const rows = [headerRow, ...dataRows].map((r) => r.map(escape).join(','));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const exportSimpleDimCSV = (rows, dimLabel, filename) => {
    if (!rows || rows.length === 0) return;
    const hasState = rows.some((r) => r && r.state);
    const headers = isCombined
      ? ['Rank', ...(hasState ? ['State'] : []), dimLabel, 'EV Sales', 'ICE Sales', 'Total Sales']
      : ['Rank', ...(hasState ? ['State'] : []), dimLabel, 'Total Sales'];
    const dataRows = rows.map((r, i) => {
      const stateCell = hasState ? [r.state || ''] : [];
      return isCombined
        ? [i + 1, ...stateCell, r.dim, r.evSales, r.iceSales, r.totalSales]
        : [i + 1, ...stateCell, r.dim, r.totalSales];
    });
    downloadRowsAsCSV(headers, dataRows, filename);
  };

  const exportCityCSV = () =>
    exportSimpleDimCSV(cityTableData, 'City', `city_${cfg.label.toLowerCase()}_sales_${filters.year || 'all'}.csv`);
  const exportSubDistrictCSV = () =>
    exportSimpleDimCSV(subDistrictTableData, 'Sub District', `sub_district_${cfg.label.toLowerCase()}_sales_${filters.year || 'all'}.csv`);

  const exportGeoDimCSV = () => {
    if (!geoDimTableData.length || !effectiveGeoDim) return;
    const includeRtoCode = effectiveGeoDim.key === 'rto';
    const includeRtoCodeContext = effectiveGeoDim.key === 'rtoCode';
    const rtoGeoCols = includeRtoCode ? ['RTO Code', 'City', 'Sub District'] : [];
    const rtoCodePrefixCols = includeRtoCodeContext ? ['State', 'City'] : [];
    const makerCols = geoDimMakersList || [];

    const headerRow = isCombined
      ? ['Rank', ...rtoCodePrefixCols, effectiveGeoDim.label, ...rtoGeoCols, ...makerCols, 'EV Sales', 'ICE Sales', 'Total Sales']
      : ['Rank', ...rtoCodePrefixCols, effectiveGeoDim.label, ...rtoGeoCols, ...makerCols, 'Total Sales'];

    const dataRows = geoDimTableData.map((r, i) => {
      const rtoCells = includeRtoCode ? [r.rtoCode || '', r.district || '', r.city || ''] : [];
      const rtoPrefixCells = includeRtoCodeContext ? [r.state || '', r.district || ''] : [];
      const makerCells = makerCols.map((mk) => r.makerCounts?.get(mk) || 0);
      const tail = isCombined ? [r.evSales, r.iceSales, r.totalSales] : [r.totalSales];
      return [i + 1, ...rtoPrefixCells, r.dim, ...rtoCells, ...makerCells, ...tail];
    });

    downloadRowsAsCSV(headerRow, dataRows, `${effectiveGeoDim.key}_sales_${filters.year || 'all'}.csv`);
  };

  // ── Geo dimension bar chart data (Top 10) ────────────────────────────────
  const geoDimChartData = useMemo(() => {
    if (!geoDimTableData.length || !effectiveGeoDim) return null;
    const top = geoDimTableData.slice(0, 10);
    const includeRtoCode = effectiveGeoDim.key === 'rto';
    const labels = top.map((r) => (
      includeRtoCode && r.rtoCode ? `${r.dim} (${r.rtoCode})` : r.dim
    ));
    if (isCombined) {
      return {
        labels,
        datasets: [
          {
            label: 'EV Sales',
            data: top.map((r) => r.evSales),
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: top.map((r) => r.iceSales),
            backgroundColor: 'rgba(255, 99, 132, 0.7)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [{
        label: `${cfg.label} Sales`,
        data: top.map((r) => r.totalSales),
        backgroundColor: top.map((_, i) => yearlyChartColors[i % yearlyChartColors.length]),
        borderColor: top.map((_, i) => yearlyBorderColors[i % yearlyBorderColors.length]),
        borderWidth: 1,
      }],
    };
  }, [geoDimTableData, effectiveGeoDim, isCombined, cfg.label, yearlyChartColors, yearlyBorderColors]);

  const buildSimpleDimTable = useCallback((yData, evYData, iceYData, dimKey) => {
    if (!yData || !yData.length) return [];
    if (!yData[0] || yData[0][dimKey] === undefined) return [];

    const combined = new Map();
    // Track the parent state name for each dim (city / sub-district) so the table
    // can render "State — Dim" without an extra round-trip.
    const stateByDim = new Map();
    yData.forEach((d) => {
      const k = String(d[dimKey]);
      combined.set(k, (combined.get(k) || 0) + (d.totalSales || 0));
      if (d.state && !stateByDim.has(k)) stateByDim.set(k, String(d.state));
    });

    let evMap = new Map();
    let iceMap = new Map();
    if (isCombined) {
      (evYData || []).forEach((d) => {
        const k = String(d[dimKey]);
        evMap.set(k, (evMap.get(k) || 0) + (d.totalSales || 0));
        if (d.state && !stateByDim.has(k)) stateByDim.set(k, String(d.state));
      });
      (iceYData || []).forEach((d) => {
        const k = String(d[dimKey]);
        iceMap.set(k, (iceMap.get(k) || 0) + (d.totalSales || 0));
        if (d.state && !stateByDim.has(k)) stateByDim.set(k, String(d.state));
      });
    }

    return [...combined.entries()]
      .map(([dim, total]) => ({
        dim,
        state: stateByDim.get(dim) || '',
        evSales: evMap.get(dim) || 0,
        iceSales: iceMap.get(dim) || 0,
        totalSales: total,
      }))
      .sort((a, b) => b.totalSales - a.totalSales);
  }, [isCombined]);

  const cityTableData = useMemo(
    () => buildSimpleDimTable(cityDimData, evCityDimData, iceCityDimData, 'district'),
    [buildSimpleDimTable, cityDimData, evCityDimData, iceCityDimData]
  );
  const subDistrictTableData = useMemo(
    () => buildSimpleDimTable(subDistrictDimData, evSubDistrictDimData, iceSubDistrictDimData, 'city'),
    [buildSimpleDimTable, subDistrictDimData, evSubDistrictDimData, iceSubDistrictDimData]
  );

  const cityChartData = useMemo(() => {
    if (!cityTableData.length) return null;
    const top = cityTableData.slice(0, 10);
    const labels = top.map((r) => r.dim);
    if (isCombined) {
      return {
        labels,
        datasets: [
          {
            label: 'EV Sales',
            data: top.map((r) => r.evSales),
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: top.map((r) => r.iceSales),
            backgroundColor: 'rgba(255, 99, 132, 0.7)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [{
        label: `${cfg.label} Sales`,
        data: top.map((r) => r.totalSales),
        backgroundColor: top.map((_, i) => yearlyChartColors[i % yearlyChartColors.length]),
        borderColor: top.map((_, i) => yearlyBorderColors[i % yearlyBorderColors.length]),
        borderWidth: 1,
      }],
    };
  }, [cityTableData, isCombined, cfg.label, yearlyChartColors, yearlyBorderColors]);

  const subDistrictChartData = useMemo(() => {
    if (!subDistrictTableData.length) return null;
    const top = subDistrictTableData.slice(0, 10);
    const labels = top.map((r) => r.dim);
    if (isCombined) {
      return {
        labels,
        datasets: [
          {
            label: 'EV Sales',
            data: top.map((r) => r.evSales),
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'ICE Sales',
            data: top.map((r) => r.iceSales),
            backgroundColor: 'rgba(255, 99, 132, 0.7)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [{
        label: `${cfg.label} Sales`,
        data: top.map((r) => r.totalSales),
        backgroundColor: top.map((_, i) => yearlyChartColors[i % yearlyChartColors.length]),
        borderColor: top.map((_, i) => yearlyBorderColors[i % yearlyBorderColors.length]),
        borderWidth: 1,
      }],
    };
  }, [subDistrictTableData, isCombined, cfg.label, yearlyChartColors, yearlyBorderColors]);

  // ── Accent color based on type ────────────────────────────────────────────
  const accentColor = selectedTypes.length > 1
    ? theme.palette.secondary.main
    : selectedTypes[0] === 'ev'
      ? theme.palette.info.main
      : theme.palette.error.main;

  const typeChartKey = (filters.vehicleType || 'ev').replace(/,/g, '-');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {/* ── Sticky header ─────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}>
        {/* Title row */}
        <Box sx={{
          px: 2, pt: 1, pb: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5,
        }}>
          <Box>
            <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
              Vehicle Sales Analytics
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last Updated — EV: {lastUpdated.ev} | ICE: {lastUpdated.ice}
            </Typography>
          </Box>
          <Chip
            icon={cfg.icon}
            label={
              selectedTypes.length > 1
                ? 'EV + ICE — Combined view'
                : `${cfg.label} — ${selectedTypes[0] === 'ev' ? 'Electric Vehicles' : 'Internal Combustion Engine Vehicles'}`
            }
            size="small"
            sx={{
              backgroundColor: alpha(accentColor, 0.1),
              color: accentColor,
              fontWeight: 600,
              border: `1px solid ${alpha(accentColor, 0.3)}`,
            }}
          />
        </Box>

        {/* Filter panel */}
        <FilterPanel
          onFilterChange={handleFilterChange}
          defaultFilters={{ vehicleType: filters.vehicleType }}
          showTypeFilter={true}
          expanded={expanded}
          setExpanded={setExpanded}
        />

        {/* Tabs */}
        <Box sx={{ px: 2, mt: 0.25, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Sales Overview" />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CompareArrowsIcon fontSize="small" />
                  Maker Comparison
                  {selectedMakers.length > 0 && (
                    <Chip label={selectedMakers.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>
      </Box>

      {/* ── Scrollable content ────────────────────────────────────────────── */}
      <Box
        ref={contentRef}
        sx={{
          px: 2, pt: 2, pb: 4,
        }}
      >

        {/* ════════════════════════════════════════════════════════════════
            TAB 0 — Sales Overview
        ════════════════════════════════════════════════════════════════ */}
        {tabValue === 0 && (
          <>
            {/* Summary stats strip */}
            <Box sx={{
              display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap',
            }}>
              {[
                { label: `Total Makers`, value: salesData.length, color: accentColor },
                ...(isCombined
                  ? [
                      { label: 'EV Sales', value: totalEvSales.toLocaleString(), color: theme.palette.info.main },
                      { label: 'ICE Sales', value: totalIceSales.toLocaleString(), color: theme.palette.error.main },
                      { label: 'Total Sales', value: totalSales.toLocaleString(), color: accentColor },
                    ]
                  : [
                      { label: 'Total Sales', value: totalSales.toLocaleString(), color: accentColor },
                    ]
                ),
                { label: 'Top 10 Share', value: `${marketShare}%`, color: accentColor },
              ].map((stat) => (
                <Paper key={stat.label} elevation={0} sx={{
                  px: 2.5, py: 1.5, flex: '1 1 140px', minWidth: 120,
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  borderLeft: `4px solid ${stat.color}`,
                  borderRadius: 1,
                  backgroundColor: alpha(stat.color, 0.04),
                }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={stat.color}>
                    {stat.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Breakdown charts — when any filter has multiple values */}
            {showBreakdown && yearlyData.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <ChartContainer
                    title={`${activeMultiDim ? `${activeMultiDim.label}-wise` : 'Year-wise'} Total ${cfg.label} Sales${filterLabel}`}
                    description={filterDescription}
                    isLoading={isLoading.yearly}
                    chartRef={!isLoading.yearly ? yearlyChartRef : null}
                    chartId={`yearly-${typeChartKey}-total`}
                    chartType="bar"
                    height="auto"
                  >
                    <Box sx={{ height: 320 }}>
                      <Bar ref={yearlyChartRef} data={yearlyTotalChartData} options={makeChartOptions({
                        plugins: {
                          legend: { display: true, position: 'top' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}`,
                            },
                          },
                        },
                      })} />
                    </Box>
                    <ChartDataTable
                      headers={breakdownTotalTableData.headers}
                      rows={breakdownTotalTableData.rows}
                      filename={`${activeMultiDim?.label || 'yearly'}_total_${cfg.label.toLowerCase()}_sales`}
                      accentColor={accentColor}
                    />
                  </ChartContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ChartContainer
                    title={`${activeMultiDim ? `${activeMultiDim.label}-wise` : 'Year-wise'} Monthly ${cfg.label} Sales${filterLabel}`}
                    description={filterDescription}
                    isLoading={isLoading.yearly}
                    chartRef={null}
                    chartId={`yearly-${typeChartKey}-monthly`}
                    chartType="line"
                    height="auto"
                  >
                    <Box sx={{ height: 320 }}>
                      <Line data={yearlyMonthlyChartData} options={makeChartOptions({
                        plugins: {
                          legend: { display: true, position: 'top' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}`,
                            },
                          },
                        },
                      })} />
                    </Box>
                    <ChartDataTable
                      headers={breakdownMonthlyTableData.headers}
                      rows={breakdownMonthlyTableData.rows}
                      filename={`${activeMultiDim?.label || 'yearly'}_monthly_${cfg.label.toLowerCase()}_sales`}
                      accentColor={accentColor}
                    />
                  </ChartContainer>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} alignItems="stretch">
              {/* Top 10 Makers — Bar chart */}
              <Grid item xs={12} md={geoDimChartData && effectiveGeoDim ? 6 : 12} sx={{ display: 'flex' }}>
                <ChartContainer
                  title={`Top 10 ${cfg.label} Makers by Sales${filterLabel}`}
                  description={filterDescription}
                  isLoading={isLoading.makers}
                  chartRef={!isLoading.makers ? makersChartRef : null}
                  chartId={`top-${typeChartKey}-makers`}
                  chartType="bar"
                  height="auto"
                  sx={{ flex: 1 }}
                >
                  <Box sx={{ height: 420 }}>
                    <Bar ref={makersChartRef} data={makersChartData} options={makeChartOptions(
                      isCombined ? {
                        plugins: {
                          legend: { display: true, position: 'top' },
                          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } },
                        },
                      } : {}
                    )} />
                  </Box>
                  <ChartDataTable
                    headers={top10MakersTableData.headers}
                    rows={top10MakersTableData.rows}
                    filename={`top10_${cfg.label.toLowerCase()}_makers_${filters.year || 'all'}`}
                    accentColor={accentColor}
                  />
                </ChartContainer>
              </Grid>

              {/* Top 10 RTOs / geo-dimension — Bar chart + ranked table together */}
              {geoDimChartData && effectiveGeoDim && (
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <ChartContainer
                    title={`All ${effectiveGeoDim.label}s — ${cfg.label} Sales${filterLabel}`}
                    description={filterDescription}
                    isLoading={isLoading.yearly}
                    chartRef={!isLoading.yearly ? geoDimChartRef : null}
                    chartId={`top-${typeChartKey}-${effectiveGeoDim.key}`}
                    chartType="bar"
                    height="auto"
                    sx={{ flex: 1 }}
                    actions={
                      <Tooltip title="Download CSV">
                        <span>
                          <IconButton size="small" onClick={exportGeoDimCSV} disabled={!geoDimTableData.length}>
                            <DownloadIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <Box sx={{ height: 280 }}>
                      <Bar ref={geoDimChartRef} data={geoDimChartData} options={makeChartOptions(
                        isCombined ? {
                          plugins: {
                            legend: { display: true, position: 'top' },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}`,
                                title: (items) => {
                                  const t = (items && items[0] && items[0].label) ? String(items[0].label) : '';
                                  return t;
                                },
                              }
                            },
                          },
                        } : {}
                      )} />
                    </Box>
                    {geoDimTableData.length > 0 && (
                      <Box sx={{ maxHeight: 350, overflow: 'auto', mt: 1 }}>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {(() => {
                                  const includeRtoCode = effectiveGeoDim.key === 'rto';
                                  const includeRtoCodeContext = effectiveGeoDim.key === 'rtoCode';
                                  const rtoGeoCols = includeRtoCode ? ['RTO Code', 'City', 'Sub District'] : [];
                                  // For RTO Code dim, "State" and "City" appear in front of the dim column.
                                  const rtoCodePrefixCols = includeRtoCodeContext ? ['State', 'City'] : [];
                                  const makerCols = geoDimMakersList || [];
                                  const headers = isCombined
                                    ? ['Rank', ...rtoCodePrefixCols, effectiveGeoDim.label, ...rtoGeoCols, ...makerCols, 'EV Sales', 'ICE Sales', 'Total Sales']
                                    : ['Rank', ...rtoCodePrefixCols, effectiveGeoDim.label, ...rtoGeoCols, ...makerCols, 'Total Sales'];
                                  const makerSet = new Set(makerCols);

                                  return headers.map((h) => {
                                  const isNumeric = h.includes('Sales') || makerSet.has(h);
                                  return (
                                    <TableCell
                                      key={h}
                                      align={isNumeric ? 'right' : 'left'}
                                      sx={{
                                        fontWeight: 700,
                                        backgroundColor: accentColor,
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {h}
                                    </TableCell>
                                  );
                                  });
                                })()}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {geoDimTableData.map((row, index) => (
                                <TableRow
                                  key={row.rtoCode ? `${row.dim}-${row.rtoCode}` : row.dim}
                                  hover
                                  sx={{
                                    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                                    ...(index < 3 && {
                                      backgroundColor: alpha(accentColor, 0.06 - index * 0.015),
                                    }),
                                    '&:hover': { backgroundColor: alpha(accentColor, 0.08) },
                                  }}
                                >
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                    {index + 1}
                                    {index < 10 && (
                                      <Chip
                                        label="Top 10"
                                        size="small"
                                        sx={{
                                          ml: 0.75, height: 16, fontSize: '0.65rem',
                                          backgroundColor: alpha(accentColor, 0.15),
                                          color: accentColor,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  {effectiveGeoDim.key === 'rtoCode' && (
                                    <>
                                      <TableCell sx={{ py: 0.8, fontSize: '0.78rem', color: theme.palette.text.secondary, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {row.state || '—'}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.8, fontSize: '0.78rem', color: theme.palette.text.secondary, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {row.district || '—'}
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: index < 3 ? 600 : 400 }}>
                                    {row.dim}
                                  </TableCell>
                                  {effectiveGeoDim.key === 'rto' && (
                                    <>
                                      <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                        {row.rtoCode || '—'}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                        {row.district || '—'}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                        {row.city || '—'}
                                      </TableCell>
                                    </>
                                  )}
                                  {(geoDimMakersList || []).map((mk) => {
                                    const v = row.makerCounts?.get(mk) || 0;
                                    return (
                                      <TableCell
                                        key={mk}
                                        align="right"
                                        sx={{
                                          py: 0.8,
                                          fontSize: '0.85rem',
                                          color: v ? 'text.primary' : 'text.disabled',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {v ? v.toLocaleString() : '—'}
                                      </TableCell>
                                    );
                                  })}
                                  {isCombined && (
                                    <>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.info.main, fontWeight: 500 }}>
                                        {row.evSales.toLocaleString()}
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.error.main, fontWeight: 500 }}>
                                        {row.iceSales.toLocaleString()}
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: isCombined ? 700 : 400 }}>
                                    {row.totalSales.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </ChartContainer>
                </Grid>
              )}

              {/* Separate City + Sub District charts (same style as RTO) */}
              {cityChartData && (
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <ChartContainer
                    title={`All Citys — ${cfg.label} Sales${filterLabel}`}
                    description={filterDescription}
                    isLoading={isLoading.yearly}
                    chartRef={null}
                    chartId={`top-${typeChartKey}-city`}
                    chartType="bar"
                    height="auto"
                    sx={{ flex: 1 }}
                    actions={
                      <Tooltip title="Download CSV">
                        <span>
                          <IconButton size="small" onClick={exportCityCSV} disabled={!cityTableData.length}>
                            <DownloadIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <Box sx={{ height: 280 }}>
                      <Bar data={cityChartData} options={makeChartOptions(
                        isCombined ? {
                          plugins: {
                            legend: { display: true, position: 'top' },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } },
                          },
                        } : {}
                      )} />
                    </Box>
                    {cityTableData.length > 0 && (
                      <Box sx={{ maxHeight: 350, overflow: 'auto', mt: 1 }}>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {(isCombined
                                  ? ['Rank', 'State', 'City', 'EV Sales', 'ICE Sales', 'Total Sales']
                                  : ['Rank', 'State', 'City', 'Total Sales']
                                ).map((h) => {
                                  const isNumeric = h.includes('Sales');
                                  return (
                                    <TableCell
                                      key={h}
                                      align={isNumeric ? 'right' : 'left'}
                                      sx={{
                                        fontWeight: 700,
                                        backgroundColor: accentColor,
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {h}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cityTableData.map((row, index) => (
                                <TableRow
                                  key={`${row.dim}-${index}`}
                                  hover
                                  sx={{
                                    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                                    ...(index < 3 && { backgroundColor: alpha(accentColor, 0.06 - index * 0.015) }),
                                    '&:hover': { backgroundColor: alpha(accentColor, 0.08) },
                                  }}
                                >
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                    {index + 1}
                                    {index < 10 && (
                                      <Chip
                                        label="Top 10"
                                        size="small"
                                        sx={{
                                          ml: 0.75, height: 16, fontSize: '0.65rem',
                                          backgroundColor: alpha(accentColor, 0.15),
                                          color: accentColor,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ py: 0.8, fontSize: '0.78rem', color: theme.palette.text.secondary, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {row.state || '—'}
                                  </TableCell>
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: index < 3 ? 600 : 400 }}>
                                    {row.dim}
                                  </TableCell>
                                  {isCombined && (
                                    <>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.info.main, fontWeight: 500 }}>
                                        {row.evSales.toLocaleString()}
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.error.main, fontWeight: 500 }}>
                                        {row.iceSales.toLocaleString()}
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: isCombined ? 700 : 400 }}>
                                    {row.totalSales.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </ChartContainer>
                </Grid>
              )}

              {subDistrictChartData && (
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <ChartContainer
                    title={`All Sub Districts — ${cfg.label} Sales${filterLabel}`}
                    description={filterDescription}
                    isLoading={isLoading.yearly}
                    chartRef={null}
                    chartId={`top-${typeChartKey}-subdistrict`}
                    chartType="bar"
                    height="auto"
                    sx={{ flex: 1 }}
                    actions={
                      <Tooltip title="Download CSV">
                        <span>
                          <IconButton size="small" onClick={exportSubDistrictCSV} disabled={!subDistrictTableData.length}>
                            <DownloadIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <Box sx={{ height: 280 }}>
                      <Bar data={subDistrictChartData} options={makeChartOptions(
                        isCombined ? {
                          plugins: {
                            legend: { display: true, position: 'top' },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } },
                          },
                        } : {}
                      )} />
                    </Box>
                    {subDistrictTableData.length > 0 && (
                      <Box sx={{ maxHeight: 350, overflow: 'auto', mt: 1 }}>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {(isCombined
                                  ? ['Rank', 'State', 'Sub District', 'EV Sales', 'ICE Sales', 'Total Sales']
                                  : ['Rank', 'State', 'Sub District', 'Total Sales']
                                ).map((h) => {
                                  const isNumeric = h.includes('Sales');
                                  return (
                                    <TableCell
                                      key={h}
                                      align={isNumeric ? 'right' : 'left'}
                                      sx={{
                                        fontWeight: 700,
                                        backgroundColor: accentColor,
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {h}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subDistrictTableData.map((row, index) => (
                                <TableRow
                                  key={`${row.dim}-${index}`}
                                  hover
                                  sx={{
                                    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                                    ...(index < 3 && { backgroundColor: alpha(accentColor, 0.06 - index * 0.015) }),
                                    '&:hover': { backgroundColor: alpha(accentColor, 0.08) },
                                  }}
                                >
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                    {index + 1}
                                    {index < 10 && (
                                      <Chip
                                        label="Top 10"
                                        size="small"
                                        sx={{
                                          ml: 0.75, height: 16, fontSize: '0.65rem',
                                          backgroundColor: alpha(accentColor, 0.15),
                                          color: accentColor,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ py: 0.8, fontSize: '0.78rem', color: theme.palette.text.secondary, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {row.state || '—'}
                                  </TableCell>
                                  <TableCell sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: index < 3 ? 600 : 400 }}>
                                    {row.dim}
                                  </TableCell>
                                  {isCombined && (
                                    <>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.info.main, fontWeight: 500 }}>
                                        {row.evSales.toLocaleString()}
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.error.main, fontWeight: 500 }}>
                                        {row.iceSales.toLocaleString()}
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: isCombined ? 700 : 400 }}>
                                    {row.totalSales.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </ChartContainer>
                </Grid>
              )}

              {/* Monthly Trend — Line chart + table */}
              <Grid item xs={12}>
                <ChartContainer
                  title={`Monthly ${cfg.label} Sales Trend${filterLabel}`}
                  description={filterDescription}
                  isLoading={isLoading.monthlyTrend}
                  chartRef={!isLoading.monthlyTrend ? monthlyTrendChartRef : null}
                  chartId={`monthly-${typeChartKey}-trend`}
                  chartType="line"
                  height="auto"
                >
                  <Box sx={{ height: 280 }}>
                    <Line ref={monthlyTrendChartRef} data={monthlyChartData} options={makeChartOptions(
                      isCombined ? {
                        plugins: {
                          legend: { display: true, position: 'top' },
                          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } },
                        },
                      } : {}
                    )} />
                  </Box>
                  <ChartDataTable
                    headers={monthlyTableData.headers}
                    rows={monthlyTableData.rows}
                    filename={`monthly_${cfg.label.toLowerCase()}_sales`}
                    accentColor={accentColor}
                  />
                </ChartContainer>
              </Grid>

              {/* Full makers table */}
              <Grid item xs={12}>
                <ChartContainer
                  title={`All ${cfg.label} Makers${filterLabel}`}
                  description={filterDescription}
                  isLoading={isLoading.tableData}
                  height="auto"
                  actions={
                    <Tooltip title="Download CSV">
                      <span>
                        <IconButton
                          size="small"
                          onClick={exportToCSV}
                          disabled={isLoading.tableData || !salesData.length}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  {salesData.length > 0 ? (
                    <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              {(isCombined
                                ? ['Rank', 'Maker', 'EV Sales', 'ICE Sales', 'Total Sales', 'Actions']
                                : ['Rank', 'Maker', 'Total Sales', 'Actions']
                              ).map((h) => {
                                const isActions = h === 'Actions';
                                const isNumeric = h.includes('Sales');
                                return (
                                  <TableCell
                                    key={h}
                                    align={isActions ? 'center' : isNumeric ? 'right' : 'left'}
                                    sx={{
                                      fontWeight: 700,
                                      backgroundColor: accentColor,
                                      color: '#fff',
                                      fontSize: '0.8rem',
                                    }}
                                  >
                                    {h}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salesData.map((row, index) => (
                              <TableRow
                                key={row._id}
                                hover
                                onClick={() => handleFilterChange({ maker: row._id })}
                                sx={{
                                  cursor: 'pointer',
                                  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                                  ...(index < 3 && {
                                    backgroundColor: alpha(accentColor, 0.06 - index * 0.015),
                                  }),
                                  '&:hover': { backgroundColor: alpha(accentColor, 0.08) },
                                }}
                              >
                                <TableCell sx={{ py: 0.8, fontSize: '0.85rem' }}>
                                  {index + 1}
                                  {index < 10 && (
                                    <Chip
                                      label="Top 10"
                                      size="small"
                                      sx={{
                                        ml: 0.75, height: 16, fontSize: '0.65rem',
                                        backgroundColor: alpha(accentColor, 0.15),
                                        color: accentColor,
                                      }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: index < 3 ? 600 : 400 }}>
                                  {row._id}
                                </TableCell>
                                {isCombined && (
                                  <>
                                    <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.info.main, fontWeight: 500 }}>
                                      {(evSalesMap.get(row._id) || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', color: theme.palette.error.main, fontWeight: 500 }}>
                                      {(iceSalesMap.get(row._id) || 0).toLocaleString()}
                                    </TableCell>
                                  </>
                                )}
                                <TableCell align="right" sx={{ py: 0.8, fontSize: '0.85rem', fontWeight: isCombined ? 700 : 400 }}>
                                  {row.totalSales.toLocaleString()}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.8 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CompareArrowsIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                      fontSize: '0.72rem', py: 0.3, px: 1,
                                      borderColor: alpha(accentColor, 0.4),
                                      color: accentColor,
                                      '&:hover': { borderColor: accentColor, backgroundColor: alpha(accentColor, 0.06) },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddMaker(row._id);
                                      setTabValue(1);
                                    }}
                                  >
                                    Compare
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No data available for the selected filters.
                    </Typography>
                  )}
                </ChartContainer>
              </Grid>

            </Grid>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            TAB 1 — Maker Comparison
        ════════════════════════════════════════════════════════════════ */}
        {tabValue === 1 && (
          <Box>
            {/* Controls */}
            <Paper sx={{ p: 2.5, mb: 2 }} elevation={1}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Compare {cfg.label} Makers Side-by-Side
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={5}>
                  <Autocomplete
                    options={allMakers.filter((m) => !selectedMakers.includes(m))}
                    renderInput={(params) => (
                      <TextField {...params} label={`Select ${cfg.label} Maker`} variant="outlined" fullWidth size="small" />
                    )}
                    onChange={(_, value) => handleAddMaker(value)}
                    value={null}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={7}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Chart type:</Typography>
                    <Button
                      size="small"
                      variant={viewType === 'bar' ? 'contained' : 'outlined'}
                      startIcon={<BarChartIcon />}
                      onClick={() => setViewType('bar')}
                      sx={viewType === 'bar'
                        ? { backgroundColor: accentColor, '&:hover': { backgroundColor: accentColor } }
                        : { borderColor: alpha(accentColor, 0.5), color: accentColor }
                      }
                    >
                      Bar
                    </Button>
                    <Button
                      size="small"
                      variant={viewType === 'line' ? 'contained' : 'outlined'}
                      startIcon={<TimelineIcon />}
                      onClick={() => setViewType('line')}
                      sx={viewType === 'line'
                        ? { backgroundColor: accentColor, '&:hover': { backgroundColor: accentColor } }
                        : { borderColor: alpha(accentColor, 0.5), color: accentColor }
                      }
                    >
                      Line
                    </Button>

                    {selectedMakers.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => setSelectedMakers([])}
                        sx={{ ml: 'auto' }}
                      >
                        Clear all
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Selected maker chips */}
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selectedMakers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Select makers from the dropdown above (or click "Compare" in the table).
                  </Typography>
                ) : (
                  selectedMakers.map((m) => (
                    <Chip
                      key={m}
                      label={m}
                      onDelete={() => handleRemoveMaker(m)}
                      deleteIcon={<ClearIcon />}
                      size="small"
                      sx={{
                        backgroundColor: alpha(accentColor, 0.1),
                        borderColor: alpha(accentColor, 0.3),
                        color: accentColor,
                        border: '1px solid',
                        fontWeight: 600,
                      }}
                    />
                  ))
                )}
              </Box>
            </Paper>

            {/* Comparison chart */}
            {selectedMakers.length > 0 && (
              <>
                <ChartContainer
                  title={`${cfg.label} Monthly Sales Comparison`}
                  isLoading={isLoading.comparison}
                  height="420px"
                  chartRef={!isLoading.comparison ? comparisonChartRef : null}
                  chartId={`${typeChartKey}-comparison`}
                  chartType={viewType}
                >
                  {viewType === 'bar' ? (
                    <Bar ref={comparisonChartRef} data={comparisonChartData} options={comparisonOptions} />
                  ) : (
                    <Line ref={comparisonChartRef} data={comparisonChartData} options={comparisonOptions} />
                  )}
                </ChartContainer>

                {/* Comparison summary table */}
                <ChartContainer
                  title="Summary"
                  isLoading={isLoading.comparison}
                  height="auto"
                  sx={{ mt: 2 }}
                >
                  <TableContainer component={Paper} sx={{ maxHeight: 400, boxShadow: 'none' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {['Maker', 'Total Sales', 'Peak Month', 'Lowest Month', ''].map((h, i) => (
                            <TableCell
                              key={h}
                              align={i >= 1 && i <= 3 ? 'right' : i === 4 ? 'center' : 'left'}
                              sx={{ fontWeight: 700, backgroundColor: accentColor, color: '#fff', fontSize: '0.8rem' }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {compareData.map((row) => {
                          const md = row.monthlyData || [];
                          const peak = [...md].sort((a, b) => b.sales - a.sales)[0] || {};
                          const lowest = [...md].filter((m) => m.sales > 0).sort((a, b) => a.sales - b.sales)[0] || {};
                          return (
                            <TableRow key={row.maker} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                              <TableCell sx={{ fontWeight: 600 }}>{row.maker}</TableCell>
                              <TableCell align="right">{row.totalSales.toLocaleString()}</TableCell>
                              <TableCell align="right">
                                {peak.month ? `${peak.month}: ${peak.sales.toLocaleString()}` : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {lowest.month ? `${lowest.month}: ${lowest.sales.toLocaleString()}` : '—'}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" color="error" onClick={() => handleRemoveMaker(row.maker)}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </ChartContainer>
              </>
            )}

            {/* Empty state */}
            {selectedMakers.length === 0 && (
              <Box sx={{
                height: 260, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1.5,
                backgroundColor: alpha(accentColor, 0.04),
                border: `1px dashed ${alpha(accentColor, 0.3)}`,
                borderRadius: 2,
              }}>
                <CompareArrowsIcon sx={{ fontSize: 48, color: alpha(accentColor, 0.4) }} />
                <Typography variant="h6" color="text.secondary">
                  No makers selected yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the dropdown above, or click <strong>Compare</strong> next to any maker in the Overview tab.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VehicleAnalytics;